// Peer feedback — requests the person has been asked to complete and the aggregated result about
// them. Submission rules (every question answered, same API call) are unchanged; the caller passes
// the existing data and submit function.
import { useState } from "react";
import { formatNumber, formatPercent } from "./copy";
import { formatShortDate, workspaceCopy } from "./workspaceCopy";
import { MANAGED, SectionHeader, SummaryCounters, FilterTabs, WsEmpty, SkeletonRows, ReviewItem, DetailDrawer, DrawerSection, GroupLabel } from "./WorkspaceUi";

const RATINGS = [5, 4, 3, 2, 1];

export function PeerFeedbackWorkspace({ language, userId, periodLabel, periodId, questions = [], assignments = [], result: rawResult, loading, error,
    ratingLabel, submitReview, messages }) {
    const t = workspaceCopy(language);
    const f = t.feedback;
    const [tab, setTab] = useState("requests");
    const [requestFilter, setRequestFilter] = useState("all");
    const [openId, setOpenId] = useState(null);

    const tasks = assignments.filter(a => a.periodId === periodId);
    const pending = tasks.filter(a => a.status === "pending");
    const completed = tasks.filter(a => a.status === "completed");
    const result = rawResult?.subjectUserId === userId && rawResult?.period?.id === periodId ? rawResult : null;
    const responseCount = result?.summary?.responseCount || 0;
    const assignedCount = result?.summary?.assignedCount || 0;
    const minResponses = result?.config?.minResponses || 1;
    const canShow = Boolean(result?.summary?.enoughResponses && result?.summary?.overallScore != null);
    const selected = tasks.find(a => a.id === openId) || null;

    const counters = [
        { id: "pending", label: f.toGive, count: formatNumber(pending.length, language), zero: !pending.length, tone: "attention" },
        { id: "completed", label: f.given, count: formatNumber(completed.length, language), zero: !completed.length },
        { id: "received", label: f.received, count: formatNumber(responseCount, language), zero: !responseCount, help: assignedCount ? f.progress(responseCount, assignedCount) : null },
    ];
    const activeCounter = tab === "results" ? "received" : requestFilter === "all" ? null : requestFilter;
    const pickCounter = id => {
        if (id === "received") { setTab("results"); return; }
        setTab("requests");
        setRequestFilter(id === "all" ? "all" : id);
    };

    const requestRow = task => {
        const done = task.status === "completed";
        return <ReviewItem key={task.id} language={language} avatar={task.subjectUser?.name || "—"} typeLabel={f.aboutPerson}
            title={task.subjectUser?.name || "—"} person={periodLabel}
            date={done && task.completedAt ? f.sent(formatShortDate(task.completedAt, language)) : null}
            status={done ? "completed" : "my_action"} statusText={done ? t.status.completed : t.status.my_action}
            cta={done ? null : f.start} ctaPrimary onOpen={done ? null : () => setOpenId(task.id)} />;
    };
    const visibleRequests = requestFilter === "pending" ? pending : requestFilter === "completed" ? completed : tasks;

    return <div className="cvi-ws-page cvi-ws-feedback-page" {...MANAGED}>
        <SectionHeader title={f.page} subtitle={[f.pageSubtitle, periodLabel].filter(Boolean).join(" · ")} />
        {error && <div className="cvi-ws-callout is-danger" role="alert">{error}</div>}
        <SummaryCounters label={f.page} value={activeCounter} onChange={pickCounter} items={counters} />
        <FilterTabs label={f.page} value={tab} onChange={setTab} options={[
            { id: "requests", label: f.tabs.requests, count: pending.length || undefined },
            { id: "results", label: f.tabs.results },
        ]} />
        {tab === "requests" && <section className="cvi-ws-panel" aria-label={f.tabs.requests}>
            {loading ? <SkeletonRows count={3} label={t.common.loading} />
                : visibleRequests.length === 0 ? <WsEmpty tone={requestFilter === "pending" || tasks.length ? "calm" : "neutral"} icon={tasks.length ? "check" : "peer"}
                    title={requestFilter === "pending" && tasks.length ? t.workCenter.emptyActions[0] : f.emptyRequests[0]}
                    description={requestFilter === "pending" && tasks.length ? null : f.emptyRequests[1]} />
                : requestFilter === "all" && pending.length && completed.length ? <>
                    <GroupLabel count={formatNumber(pending.length, language)}>{f.toGive}</GroupLabel>
                    <ul className="cvi-ws-review-list">{pending.map(requestRow)}</ul>
                    <GroupLabel count={formatNumber(completed.length, language)}>{f.given}</GroupLabel>
                    <ul className="cvi-ws-review-list">{completed.map(requestRow)}</ul>
                </> : <ul className="cvi-ws-review-list">{visibleRequests.map(requestRow)}</ul>}
        </section>}
        {tab === "results" && (loading ? <section className="cvi-ws-panel"><SkeletonRows count={2} label={t.common.loading} /></section>
            : !result?.config?.enabled ? <WsEmpty tone="neutral" icon="peer" title={f.notEnabled[0]} description={f.notEnabled[1]} />
            : !canShow ? <WsEmpty tone="neutral" icon="schedule" title={f.threshold(minResponses)} description={f.progress(responseCount, assignedCount)} />
            : <section className="cvi-ws-panel cvi-ws-panel-pad">
                <div className="cvi-ws-figure">
                    <strong>{formatPercent((Number(result.summary.overallScore) || 0) / 5 * 100, language)}</strong>
                    <span>{f.overall} · {f.responses(responseCount)}</span>
                </div>
                <div style={{ marginTop: 12 }}>
                    {result.questions.map(question => {
                        const pct = question.averageScore == null ? 0 : (Number(question.averageScore) / 5) * 100;
                        return <div key={question.questionId} className="cvi-ws-score-row">
                            <span>{question.label}</span>
                            <strong>{question.averageScore == null ? "—" : formatPercent(pct, language)}</strong>
                            <span className="cvi-ws-score-bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
                            <small>{f.responses(question.responseCount)} · {f.weight(question.weight)}</small>
                        </div>;
                    })}
                </div>
                {result.config.visibility === "anonymous" && <p className="cvi-ws-muted" style={{ marginTop: 12 }}>{f.anonymous}</p>}
            </section>)}
        {selected && <PeerFeedbackForm key={selected.id} language={language} task={selected} questions={questions} periodLabel={periodLabel}
            ratingLabel={ratingLabel} submitReview={submitReview} messages={messages} onClose={() => setOpenId(null)} />}
    </div>;
}

function PeerFeedbackForm({ language, task, questions, periodLabel, ratingLabel, submitReview, messages, onClose }) {
    const t = workspaceCopy(language);
    const [answers, setAnswers] = useState(() => {
        const existing = {};
        (task.review?.answers || []).forEach(answer => { existing[answer.questionId] = Number(answer.score); });
        return existing;
    });
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if (saving) return;
        if (questions.some(question => answers[question.id] == null)) { setError(messages.allRequired); return; }
        setSaving(true); setError("");
        try {
            await submitReview(task.id, answers);
            onClose();
        } catch (submitError) {
            console.error("Peer feedback submission failed:", submitError);
            setError(submitError.message || messages.submitFailed);
        } finally { setSaving(false); }
    };
    const answered = questions.filter(q => answers[q.id] != null).length;
    return <DetailDrawer open onClose={onClose} language={language} width="lg" typeLabel={t.feedback.page} typeIcon="peer"
        title={t.feedback.requestedFor(task.subjectUser?.name || "—")} status="my_action" statusText={t.status.my_action}
        headerExtra={<span className="cvi-ws-muted">{[periodLabel, `${answered}/${questions.length}`].filter(Boolean).join(" · ")}</span>}
        footer={<>
            {error && <p className="cvi-ws-error" role="alert">{error}</p>}
            <div className="cvi-ws-decision">
                <button type="button" className="cvi-ws-btn is-secondary" onClick={onClose}>{t.common.close}</button>
                <button type="button" className="cvi-ws-btn is-primary" disabled={saving} onClick={submit}>{saving ? messages.submitting : messages.submit}</button>
            </div>
        </>}>
        <DrawerSection>
            {questions.map((question, index) => <div key={question.id} className="cvi-ws-question">
                <p>{index + 1}. {question.label}</p>
                <div className="cvi-ws-choices" role="radiogroup" aria-label={question.label}>
                    {RATINGS.map(score => <button key={score} type="button" className="cvi-ws-choice" role="radio"
                        aria-checked={Number(answers[question.id]) === score}
                        onClick={() => setAnswers(current => ({ ...current, [question.id]: score }))}>
                        <strong>{ratingLabel(score)}</strong>
                    </button>)}
                </div>
            </div>)}
        </DrawerSection>
    </DetailDrawer>;
}
